import { useId } from 'react';

interface StatusSegmentedOption<T extends string> {
    value: T;
    label: string;
}

interface StatusSegmentedControlProps<T extends string> {
    label: string;
    options: readonly StatusSegmentedOption<T>[];
    value: T | null;
    onChange: (value: T | null) => void;
}

const SEGMENT_BASE_CLASSES = 'px-3 py-1.5 rounded-md text-sm font-medium transition-colors';
// The radio itself is visually hidden, so its segment carries the focus ring.
const SEGMENT_FOCUS_CLASSES = 'peer-focus-visible:ring-2 peer-focus-visible:ring-gray-400';
const ALL_SEGMENT_KEY = '__all__';

// "All" plus one segment per option, one active at a time. Native radios keep
// the group a single tab stop with arrow keys, per the ARIA radio pattern.
export function StatusSegmentedControl<T extends string>({
    label,
    options,
    value,
    onChange
}: StatusSegmentedControlProps<T>) {
    const groupName = useId();
    const segments: readonly { value: T | null; label: string }[] = [
        { value: null, label: 'All' },
        ...options
    ];

    return (
        <div>
            <span className="block text-[13px] font-semibold text-gray-700 mb-1.5">{label}</span>
            <div
                role="radiogroup"
                aria-label={label}
                className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
            >
                {segments.map((segment) => {
                    const isActive = segment.value === value;
                    return (
                        <label
                            key={segment.value ?? ALL_SEGMENT_KEY}
                            className="flex cursor-pointer select-none"
                        >
                            <input
                                type="radio"
                                name={groupName}
                                className="sr-only peer"
                                checked={isActive}
                                onChange={() => onChange(segment.value)}
                            />
                            <span
                                className={`${SEGMENT_BASE_CLASSES} ${SEGMENT_FOCUS_CLASSES} ${
                                    isActive
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {segment.label}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
